import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

const dishes = [
  {
    name: 'Butter Chicken',
    desc: 'Silky tomato-butter sauce with tender grilled chicken and fenugreek',
    price: '$17',
    tag: 'House Favourite',
    tagColor: 'bg-saffron',
    menuHash: 'curries',
    image: 'https://readdy.ai/api/search-image?query=butter%20chicken%20murgh%20makhani%20rich%20creamy%20orange%20tomato%20butter%20sauce%20tender%20chicken%20pieces%20black%20bowl%20cream%20swirl%20green%20coriander%20garnish%20elegant%20Indian%20restaurant%20food%20photography%20warm%20vibrant%20close%20up%20appetizing&width=400&height=500&seq=1201&orientation=portrait',
  },
  {
    name: 'Tandoori Platter',
    desc: 'Assorted kebabs from our clay oven — chicken tikka, seekh kebab and lamb chops',
    price: '$26',
    tag: "Chef's Pick",
    tagColor: 'bg-coral',
    menuHash: 'tandoor',
    image: 'https://readdy.ai/api/search-image?query=Indian%20tandoori%20mixed%20platter%20chicken%20tikka%20seekh%20kebab%20lamb%20chops%20sizzling%20iron%20plate%20sliced%20onions%20lemon%20mint%20chutney%20garnish%20dark%20background%20restaurant%20food%20photography%20dramatic%20warm%20saffron%20lighting&width=400&height=500&seq=1202&orientation=portrait',
  },
  {
    name: 'Lamb Biryani',
    desc: 'Slow-cooked aromatic basmati layered with succulent lamb and saffron',
    price: '$20',
    tag: 'Bestseller',
    tagColor: 'bg-turmeric',
    menuHash: 'biryani',
    image: 'https://readdy.ai/api/search-image?query=lamb%20biryani%20fragrant%20saffron%20basmati%20rice%20tender%20lamb%20pieces%20fried%20onions%20fresh%20mint%20coriander%20served%20in%20traditional%20copper%20handi%20pot%20overhead%20shot%20Indian%20restaurant%20food%20photography%20warm%20golden%20tones&width=400&height=500&seq=1203&orientation=portrait',
  },
  {
    name: 'Samosa Chaat',
    desc: 'Crispy pastry with chickpeas, tangy chutneys, yogurt and pomegranate',
    price: '$10',
    tag: 'Street Food',
    tagColor: 'bg-teal',
    menuHash: 'appetizers',
    image: 'https://readdy.ai/api/search-image?query=samosa%20chaat%20Indian%20street%20food%20crispy%20golden%20pastry%20chickpeas%20colorful%20green%20tamarind%20chutney%20white%20yogurt%20pomegranate%20seeds%20crispy%20sev%20vibrant%20plate%20professional%20food%20photography%20close%20up&width=400&height=500&seq=1204&orientation=portrait',
  },
  {
    name: 'Paneer Tikka',
    desc: 'Marinated cottage cheese grilled to smoky perfection with bell peppers',
    price: '$15',
    tag: 'Vegetarian',
    tagColor: 'bg-[#4CAF50]',
    menuHash: 'tandoor',
    image: 'https://readdy.ai/api/search-image?query=paneer%20tikka%20golden%20grilled%20marinated%20cottage%20cheese%20colorful%20bell%20peppers%20charred%20marks%20dark%20slate%20plate%20mint%20chutney%20lemon%20wedge%20Indian%20restaurant%20food%20photography%20warm%20saffron%20background&width=400&height=500&seq=1205&orientation=portrait',
  },
  {
    name: 'Gulab Jamun',
    desc: 'Warm milk dumplings in rose-cardamom syrup with vanilla ice cream',
    price: '$8',
    tag: 'Dessert',
    tagColor: 'bg-magenta',
    menuHash: 'desserts',
    image: 'https://readdy.ai/api/search-image?query=gulab%20jamun%20warm%20golden%20brown%20rose%20syrup%20dumplings%20vanilla%20ice%20cream%20scoop%20rose%20petals%20pistachio%20garnish%20dark%20elegant%20plate%20Indian%20dessert%20food%20photography%20romantic%20warm%20candlelight%20vibrant&width=400&height=500&seq=1206&orientation=portrait',
  },
];

export default function FeaturedDishes() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-20 lg:py-28 bg-cream">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="font-sans text-xs uppercase tracking-[0.3em] text-saffron mb-3">From Our Kitchen</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-charcoal mb-4">Signature Flavors</h2>
          <p className="font-sans text-base text-charcoal/60 max-w-md mx-auto">
            Handcrafted with love, served with passion, inspired by centuries of Indian culinary heritage
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {dishes.map((dish, i) => (
            <div
              key={dish.name}
              className={`group relative rounded-3xl overflow-hidden transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${i * 0.1}s`, aspectRatio: '4/5' }}
            >
              {/* Image */}
              <img
                src={dish.image}
                alt={dish.name}
                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />

              {/* Gradient overlay — always visible */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent"></div>

              {/* Price Tag */}
              <div className="absolute top-4 right-4 bg-saffron text-white font-sans font-bold text-sm px-3 py-1 rounded-full">
                {dish.price}
              </div>

              {/* Category Tag */}
              <div className={`absolute top-4 left-4 ${dish.tagColor} text-white font-sans text-xs font-semibold px-3 py-1 rounded-full`}>
                {dish.tag}
              </div>

              {/* Content — always visible at bottom */}
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="font-serif text-2xl text-white font-semibold mb-1">{dish.name}</h3>
                <p className="font-sans text-sm text-white/80 leading-relaxed line-clamp-2 mb-4">{dish.desc}</p>

                {/* CTA — always visible, not just on hover */}
                <Link
                  to={`/menu#${dish.menuHash}`}
                  className="inline-flex items-center gap-2 bg-saffron/90 hover:bg-saffron text-white font-sans font-semibold text-xs px-4 py-2.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  View in Menu
                  <span className="w-4 h-4 flex items-center justify-center rounded-full bg-white/25">
                    <i className="ri-arrow-right-line text-[10px]"></i>
                  </span>
                </Link>
              </div>

              {/* Glow ring on hover */}
              <div className="absolute inset-0 ring-2 ring-saffron/0 group-hover:ring-saffron/50 rounded-3xl transition-all duration-300 pointer-events-none"></div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/menu"
            className="inline-flex items-center gap-2 font-sans font-semibold text-sm text-saffron border-2 border-saffron px-8 py-3.5 rounded-full hover:bg-saffron hover:text-white transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            Explore Full Menu
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
